import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Download, Upload, Trash2, X, Search, Pencil } from "lucide-react";
import { PageHeader, PageBody, Field } from "@/components/PageChrome";
import { Input, Select, Button } from "@/components/controls";
import ImageInput from "@/components/ImageInput";
import PaginationBar from "@/components/PaginationBar";
import UniversalFilterBar from "@/components/UniversalFilterBar";
import MultiSelectFilter from "@/components/MultiSelectFilter";
import { applyFieldFilters } from "@/components/FieldFilterBar";
import { T } from "@/lib/testIds";
import { usePerms } from "@/lib/auth";
/**
 * Generic module page with dense table, search, CSV import/export, and edit drawer.
 * `columns`: array of { key, label, mono?, formatter?(row) }
 * `fields`: array of { key, label, type: text|number|select|textarea|image, options?() }
 */
export default function ModulePage({
  moduleKey,
  title,
  subtitle,
  columns,
  fields,
  idKey = "id",
  extraToolbar = null,
  onRowClick = null,
  filterFields = null,  // optional array of column keys to expose as filter inputs
  rowActions = false,   // when true, show inline Edit/Delete buttons per row (useful when onRowClick navigates away)
  topBarFilters = null, // Mar 1 2026 — array of { key, label, options } rendered as MultiSelectFilter in the toolbar; pushed to backend as ?<key>=comma,values.
}) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  // Mar 1 2026 — server-side top-bar filters (e.g. Mapping's "Warehouse").
  // Each key maps to an array of selected values → pushed to the backend as
  // ?<key>=comma,values. Kept SEPARATE from `filters` (which drives the
  // client-side FieldFilterBar) so the fetch stays cheap.
  const [topBarFilterValues, setTopBarFilterValues] = useState({});
  const [filterFieldsMeta, setFilterFieldsMeta] = useState([]); // schema for applyFieldFilters
  const [showFilter, setShowFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // record being edited (null=none)
  const [dropdowns, setDropdowns] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => Number(localStorage.getItem(`${moduleKey}_page_size`)) || 50);
  const [total, setTotal] = useState(0);
  // Feb 28 2026 — row-selection state for the Delete Selected toolbar action.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Sep 5 2026 — live progress for background CSV imports (see onImport).
  // null when no import is running; otherwise { total, processed, filename }.
  const [importJob, setImportJob] = useState(null);
  const { canEdit, canView, canDelete } = usePerms();
  const editable = canEdit(moduleKey);
  const deletable = canDelete(moduleKey);
  const visibleColumns = useMemo(() => columns.filter((c) => canView(moduleKey, c.key)), [columns, moduleKey, canView]);
  const visibleFilterFields = useMemo(() => (filterFields || []).filter((k) => canView(moduleKey, k)), [filterFields, moduleKey, canView]);

  useEffect(() => {
    api.get("/settings/dropdowns").then((r) => setDropdowns(r.data)).catch(() => {});
    // Also fetch schema so applyFieldFilters can handle typed filter values
    api.get(`/settings/schema/${moduleKey}`)
      .then((r) => setFilterFieldsMeta((r.data.fields || []).map((f) => ({ key: f.key, type: f.type }))))
      .catch(() => {});
  }, [moduleKey]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { paginated: 1, page, page_size: pageSize };
      if (q) params.q = q;
      // Mar 1 2026 — push top-bar filter selections down to the server as
      // comma-separated query params. Empty arrays are omitted so the API
      // treats them as "no filter" instead of "no values match".
      for (const [k, vals] of Object.entries(topBarFilterValues || {})) {
        if (Array.isArray(vals) && vals.length > 0) params[k] = vals.join(",");
      }
      const { data } = await api.get(`/${moduleKey}`, { params });
      const items = Array.isArray(data) ? data : (data.items || []);
      // client-side typed field filters (from UniversalFilterBar)
      const filtered = items.filter((r) => applyFieldFilters(r, filters, filterFieldsMeta));
      setRows(filtered);
      setTotal(Array.isArray(data) ? items.length : (data.total ?? 0));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [moduleKey, filters, filterFieldsMeta, page, pageSize, topBarFilterValues]);
  // When search text changes, wait for Enter (load) but reset to page 1

  const save = async () => {
    try {
      if (editing?.[idKey] && rows.find((r) => r[idKey] === editing[idKey])) {
        const { data } = await api.patch(`/${moduleKey}/${editing[idKey]}`, editing);
        setRows((rs) => rs.map((r) => (r[idKey] === data[idKey] ? data : r)));
      } else {
        const { data } = await api.post(`/${moduleKey}`, editing);
        setRows((rs) => [data, ...rs]);
      }
      toast.success("Saved");
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async () => {
    if (!editing?.[idKey]) return;
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`/${moduleKey}/${editing[idKey]}`);
      setRows((rs) => rs.filter((r) => r[idKey] !== editing[idKey]));
      setEditing(null);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const inlineDelete = async (row) => {
    if (!row?.[idKey]) return;
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`/${moduleKey}/${row[idKey]}`);
      setRows((rs) => rs.filter((r) => r[idKey] !== row[idKey]));
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onPageSizeChange = (n) => {
    localStorage.setItem(`${moduleKey}_page_size`, String(n));
    setPageSize(n);
    setPage(1);
  };

  // Feb 28 2026 — surface the DELETE-row counters if the backend returned
  // them. Old modules (no DELETE support) still work because the fallback
  // ignores undefined counters.
  const summarizeImport = (data) => {
    const parts = [];
    if (data.created)  parts.push(`${data.created} imported`);
    if (data.updated)  parts.push(`${data.updated} updated`);
    if (data.deleted)  parts.push(`${data.deleted} deleted`);
    if (data.skipped_not_found) parts.push(`${data.skipped_not_found} skipped (not found)`);
    return parts.length ? parts.join(", ") : "Import complete";
  };

  // Sep 5 2026 — the import now runs as a background job (see backend
  // routes_common.import_csv) so a large CSV doesn't hit the platform's
  // ~120s proxy timeout with no feedback. Poll /api/imports/status/{job_id}
  // every 700ms and drive a visible progress bar until the job finishes.
  const pollImportJob = (jobId, total, filename) => {
    setImportJob({ jobId, total, processed: 0, filename });
    const interval = setInterval(async () => {
      try {
        const { data: job } = await api.get(`/imports/status/${jobId}`);
        if (job.status === "running") {
          setImportJob({ jobId, total: job.total ?? total, processed: job.processed ?? 0, filename });
          return;
        }
        clearInterval(interval);
        setImportJob(null);
        if (job.status === "done") {
          toast.success(summarizeImport(job.result || {}));
          const warnings = job.result?.warnings || [];
          warnings.forEach((w) => toast.warning(w));
        } else {
          toast.error(job.error || "Import failed");
        }
        load();
      } catch (err) {
        clearInterval(interval);
        setImportJob(null);
        toast.error(formatApiError(err));
      }
    }, 700);
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append("file", f);
    try {
      const { data } = await api.post(`/${moduleKey}/import/csv`, form, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.queued && data.job_id) {
        pollImportJob(data.job_id, data.total ?? 0, f.name);
      } else {
        // Backward-compat: older backend that still returns the summary inline.
        toast.success(summarizeImport(data));
        load();
      }
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };

  // Feb 28 2026 — bulk-delete the currently-ticked rows via the new
  // POST /<module>/bulk-delete endpoint. Falls back to per-id DELETE on 404
  // so any module that hasn't shipped the batch endpoint yet still works.
  const runBulkDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      let deletedCount = 0;
      try {
        const { data } = await api.post(`/${moduleKey}/bulk-delete`, { ids });
        deletedCount = data?.deleted ?? ids.length;
      } catch (err) {
        if (err?.response?.status === 404) {
          for (const id of ids) { await api.delete(`/${moduleKey}/${id}`); }
          deletedCount = ids.length;
        } else { throw err; }
      }
      setRows((rs) => rs.filter((r) => !selectedIds.has(r[idKey])));
      setSelectedIds(new Set());
      setBulkDeleteConfirm(false);
      toast.success(`${deletedCount} row${deletedCount === 1 ? "" : "s"} deleted.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkDeleting(false); }
  };

  // Header checkbox toggles every row currently visible on this page.
  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r[idKey]));
  const someSelected = !allSelected && rows.some((r) => selectedIds.has(r[idKey]));
  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) rows.forEach((r) => next.delete(r[idKey]));
      else rows.forEach((r) => next.add(r[idKey]));
      return next;
    });
  };
  const toggleRow = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <>
      <PageHeader title={title} subtitle={subtitle} testId={T.moduleTitle(moduleKey)} module={moduleKey}>
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid={T.searchInput(moduleKey)}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
            placeholder="Search…"
            className="h-8 pl-7 pr-2 w-56 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        {/* Mar 1 2026 — server-side top-bar filters (e.g. Mapping's Warehouse
            dropdown). Renders as MultiSelectFilter and pushes selections to
            the backend as ?<key>=v1,v2 query params so no client-side
            filtering is done — the whole dataset never leaves Mongo. */}
        {Array.isArray(topBarFilters) && topBarFilters.map((tbf) => (
          <MultiSelectFilter
            key={tbf.key}
            label={tbf.label}
            options={tbf.options || []}
            value={topBarFilterValues[tbf.key] || []}
            onChange={(next) => {
              setPage(1);
              setTopBarFilterValues((v) => ({ ...v, [tbf.key]: next }));
            }}
            testId={`${moduleKey}-filter-${tbf.key}`}
          />
        ))}
        <Button onClick={() => downloadFile(`/${moduleKey}/export/csv`, `${moduleKey}.csv`)} testId={T.exportBtn(moduleKey)}>
          <Download size={13} /> Export
        </Button>
        <label
          className="h-8 px-3 text-[12px] font-medium rounded-sm inline-flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50 cursor-pointer"
          data-testid={T.importBtn(moduleKey)}
          title={'CSV supports an "action" column: leave blank to upsert, write DELETE to remove a row.'}
        >
          <Upload size={13} /> Import
          <input type="file" accept=".csv,.xlsx,.xls" onChange={onImport} className="hidden" disabled={!!importJob} />
        </label>
        {importJob && (
          <div
            className="flex items-center gap-2 h-8 px-2"
            data-testid={`${moduleKey}-import-progress`}
            title={`Importing ${importJob.filename || "file"}…`}
          >
            <div className="w-28 h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-300"
                style={{
                  width: importJob.total
                    ? `${Math.min(100, Math.round((importJob.processed / importJob.total) * 100))}%`
                    : "15%",
                }}
              />
            </div>
            <span className="text-[11px] text-slate-500 whitespace-nowrap">
              Importing… {importJob.processed}/{importJob.total || "?"}
            </span>
          </div>
        )}
        <Button onClick={() => setShowFilter((s) => !s)} testId={`filter-${moduleKey}-btn`}>
          <Search size={13} /> Filter
          {Object.values(filters).filter((v) => v !== undefined && v !== null && v !== "" && !(typeof v === "object" && !Object.values(v).some(Boolean))).length > 0 && (
            <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-blue-600 rounded-full">
              {Object.values(filters).filter((v) => v !== undefined && v !== null && v !== "" && !(typeof v === "object" && !Object.values(v).some(Boolean))).length}
            </span>
          )}
        </Button>
        {extraToolbar}
        {deletable && selectedIds.size > 0 && (
          <Button
            variant="danger"
            onClick={() => setBulkDeleteConfirm(true)}
            testId={`${moduleKey}-bulk-delete-btn`}
          >
            <Trash2 size={13} /> Delete {selectedIds.size} Selected
          </Button>
        )}
        {editable && (
          <Button variant="primary" onClick={() => setEditing({})} testId={T.newBtn(moduleKey)}>
            <Plus size={13} /> New
          </Button>
        )}
      </PageHeader>
      <PageBody>
        {showFilter && (
          <div className="mb-3">
            <UniversalFilterBar
              moduleKey={moduleKey}
              value={filters}
              onChange={setFilters}
            />
          </div>
        )}
        <div className="border border-slate-200 bg-white rounded-sm overflow-hidden">
          <div className="max-h-[calc(100vh-240px)] overflow-auto">
            <table className="erp-table">
              <thead>
                <tr>
                  {deletable && (
                    <th className="w-8 text-center">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => { if (el) el.indeterminate = someSelected; }}
                        onChange={toggleSelectAllVisible}
                        data-testid={`${moduleKey}-select-all`}
                        title="Select all rows on this page"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                  )}
                  {visibleColumns.map((c) => (<th key={c.key}>{c.label}</th>))}
                  {rowActions && editable && <th className="w-[110px] text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row[idKey]}
                    data-testid={T.row(moduleKey, row[idKey])}
                    onClick={() => (onRowClick ? onRowClick(row) : setEditing(row))}
                    className={`cursor-pointer ${selectedIds.has(row[idKey]) ? "bg-blue-50" : ""}`}
                  >
                    {deletable && (
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(row[idKey])}
                          onChange={() => toggleRow(row[idKey])}
                          data-testid={`${moduleKey}-row-check-${row[idKey]}`}
                        />
                      </td>
                    )}
                    {visibleColumns.map((c) => (
                      <td key={c.key} className={c.mono ? "font-mono" : ""}>
                        {c.formatter ? c.formatter(row) : (row[c.key] ?? "—")}
                      </td>
                    ))}
                    {rowActions && editable && (
                      <td className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setEditing(row); }}
                          data-testid={`row-edit-${moduleKey}-${row[idKey]}`}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-sm text-slate-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Edit"
                        >
                          <Pencil size={13} />
                        </button>
                        {deletable && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); inlineDelete(row); }}
                            data-testid={`row-delete-${moduleKey}-${row[idKey]}`}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-sm text-slate-600 hover:text-red-700 hover:bg-red-50 ml-1"
                            title="Delete"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {!rows.length && !loading && (
                  <tr><td colSpan={(visibleColumns.length || 1) + (rowActions && editable ? 1 : 0)} className="text-center py-8 text-slate-500">
                    {visibleColumns.length === 0 ? "No columns visible for your team." : (
                      <>No records. Click <b>New</b> to create one.</>
                    )}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-end">
          <PaginationBar
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={onPageSizeChange}
            loading={loading}
            testIdPrefix={moduleKey}
          />
        </div>
      </PageBody>

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={() => setEditing(null)}>
          <div
            className="w-[520px] max-w-full h-full bg-white border-l border-slate-200 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4">
              <div>
                <div className="font-heading text-[14px] font-semibold text-slate-900">
                  {editing?.[idKey] ? `Edit ${title.slice(0, -1) || title}` : `New ${title.slice(0, -1) || title}`}
                </div>
                {editing?.[idKey] && <div className="text-[11px] font-mono text-slate-500">{editing[idKey]}</div>}
              </div>
              <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-auto p-4 grid grid-cols-2 gap-3">
              {fields.filter((f) => canView(moduleKey, f.key)).map((f) => {
                const fieldEditable = editable && canEdit(moduleKey, f.key);
                return (
                <Field key={f.key} label={f.label} span={f.span || 2}>
                  {f.type === "select" ? (
                    <Select
                      testId={T.formInput(f.key)}
                      options={typeof f.options === "function" ? f.options({ dropdowns }) : (f.options || [])}
                      value={editing[f.key]}
                      onChange={(v) => setEditing((s) => ({ ...s, [f.key]: v }))}
                      disabled={!fieldEditable}
                    />
                  ) : f.type === "image" ? (
                    <ImageInput
                      testId={T.formInput(f.key)}
                      value={editing[f.key]}
                      onChange={(v) => setEditing((s) => ({ ...s, [f.key]: v }))}
                    />
                  ) : f.type === "textarea" ? (
                    <textarea
                      data-testid={T.formInput(f.key)}
                      rows={3}
                      value={editing[f.key] || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, [f.key]: e.target.value }))}
                      disabled={!fieldEditable}
                      className={`w-full px-2 py-1.5 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${!fieldEditable ? "readonly-field" : ""}`}
                    />
                  ) : (
                    <Input
                      testId={T.formInput(f.key)}
                      type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                      value={editing[f.key] ?? ""}
                      readOnly={!fieldEditable}
                      className={!fieldEditable ? "readonly-field" : ""}
                      onChange={(e) => setEditing((s) => ({ ...s, [f.key]: f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value }))}
                    />
                  )}
                </Field>
                );
              })}
            </div>
            <div className="h-14 border-t border-slate-200 flex items-center justify-between px-4">
              {editing?.[idKey] && deletable ? (
                <Button variant="danger" onClick={del} testId={T.deleteBtn}><Trash2 size={13} /> Delete</Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button onClick={() => setEditing(null)} testId={T.cancelBtn}>Cancel</Button>
                {editable && <Button variant="primary" onClick={save} testId={T.saveBtn}>Save</Button>}
              </div>
            </div>
          </div>
        </div>
      )}
      {bulkDeleteConfirm && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => !bulkDeleting && setBulkDeleteConfirm(false)}>
          <div className="bg-white rounded-md shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid={`${moduleKey}-bulk-delete-confirm`}>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                <Trash2 size={16} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-[14px] font-bold text-slate-900">Delete {selectedIds.size} selected rows?</h3>
                <p className="text-[12.5px] text-slate-600 mt-1">This cannot be undone. Each row will be removed permanently and an audit log entry will be written.</p>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <Button onClick={() => setBulkDeleteConfirm(false)} disabled={bulkDeleting} testId={`${moduleKey}-bulk-delete-cancel`}>Cancel</Button>
              <Button variant="danger" onClick={runBulkDelete} disabled={bulkDeleting} testId={`${moduleKey}-bulk-delete-confirm-btn`}>
                {bulkDeleting ? "Deleting…" : `Delete ${selectedIds.size}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
